import type { IUser } from "../../../shared/models/User"
import type { IValueRating } from "../../../shared/models/three-sixty-feedback-models/ValueRating"
import type { ILeadershipRating } from "../../../shared/models/three-sixty-feedback-models/LeadershipRating"
import type { ITemplateRating } from "../../../shared/models/three-sixty-feedback-models/TemplateRating"

interface UserScores {
  userId: string
  userName: string
  department: string
  values: number
  leadership: number
  templates: number
  overallPerformance: number
}

/**
 * Calculate individual user's performance scores across all rating types
 * Formula: Sum of all ratings for that user / count of ratings
 */
export function calculateUserScores(
  userId: string,
  user: IUser,
  valueRatings: IValueRating[],
  leadershipRatings: ILeadershipRating[],
  templateRatings: ITemplateRating[],
): UserScores {
  // Calculate Values score
  const userValueRatings = valueRatings.filter((r) => r.rateeId === userId)
  let valuesTotal = 0
  let valuesCount = 0

  userValueRatings.forEach((rating) => {
    Object.values(rating.values).forEach((raterData: any) => {
      Object.entries(raterData.ratings).forEach(([_, statements]: any) => {
        Object.values(statements).forEach((score: any) => {
          if (Array.isArray(score)) {
            score.forEach((s: number) => {
              valuesTotal += s
              valuesCount += 1
            })
          } else {
            valuesTotal += score
            valuesCount += 1
          }
        })
      })
    })
  })

  const valuesScore = valuesCount > 0 ? valuesTotal / valuesCount : 0

  // Calculate Leadership score
  const userLeadershipRatings = leadershipRatings.filter((r) => r.rateeId === userId)
  let leadershipTotal = 0
  let leadershipCount = 0

  userLeadershipRatings.forEach((rating) => {
    Object.values(rating.criteria).forEach((criteriaData: any) => {
      Object.entries(criteriaData.ratings).forEach(([_, statements]: any) => {
        Object.values(statements).forEach((score: any) => {
          if (Array.isArray(score)) {
            score.forEach((s: number) => {
              leadershipTotal += s
              leadershipCount += 1
            })
          } else {
            leadershipTotal += score
            leadershipCount += 1
          }
        })
      })
    })
  })

  const leadershipScore = leadershipCount > 0 ? leadershipTotal / leadershipCount : 0

  // Calculate Templates score
  const userTemplateRatings = templateRatings.filter((r) => r.rateeId === userId)
  let templatesTotal = 0
  let templatesCount = 0

  userTemplateRatings.forEach((rating) => {
    Object.values(rating.values).forEach((raterData: any) => {
      Object.entries(raterData.ratings).forEach(([_, statements]: any) => {
        Object.values(statements).forEach((score: any) => {
          if (Array.isArray(score)) {
            score.forEach((s: number) => {
              templatesTotal += s
              templatesCount += 1
            })
          } else {
            templatesTotal += score
            templatesCount += 1
          }
        })
      })
    })
  })

  const templatesScore = templatesCount > 0 ? templatesTotal / templatesCount : 0

  // Overall performance = average of all three scores
  const validScores = [valuesScore, leadershipScore, templatesScore].filter((s) => s > 0)
  const overallPerformance = validScores.length > 0 ? validScores.reduce((a, b) => a + b, 0) / validScores.length : 0

  const scores: UserScores = {
    userId,
    userName: user.displayName || user.email || userId,
    department: user.department || "Unknown",
    values: Number(valuesScore.toFixed(2)),
    leadership: Number(leadershipScore.toFixed(2)),
    templates: Number(templatesScore.toFixed(2)),
    overallPerformance: Number(overallPerformance.toFixed(2)),
  }

  // Log for debugging
  console.log(`[v0] User Score Report: ${scores.userName}`)
  console.log(`  Department: ${scores.department}`)
  console.log(`  Values: ${scores.values.toFixed(2)} (${valuesCount} ratings)`)
  console.log(`  Leadership: ${scores.leadership.toFixed(2)} (${leadershipCount} ratings)`)
  console.log(`  Templates: ${scores.templates.toFixed(2)} (${templatesCount} ratings)`)
  console.log(`  Overall Performance: ${scores.overallPerformance.toFixed(2)}`)

  return scores
}

/**
 * Calculate all users' scores in a department
 */
export function calculateDepartmentUserScores(
  users: IUser[],
  valueRatings: IValueRating[],
  leadershipRatings: ILeadershipRating[],
  templateRatings: ITemplateRating[],
  departmentId: string,
): UserScores[] {
  return users
    .filter((u) => u.department === departmentId)
    .map((user) => calculateUserScores(user.uid, user, valueRatings, leadershipRatings, templateRatings))
}
